import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ComponentProps, ReactNode } from "react";
import type { IconType } from "react-icons";
import { LuCircleHelp } from "react-icons/lu";

type CustomTooltipProps = {
  /** Tooltip content. */
  children: ReactNode;
  /** Custom element that opens the tooltip (rendered with asChild). */
  trigger?: ReactNode;
  /** Icon used as trigger. Defaults to the help icon when no trigger is given. */
  icon?: IconType;
  iconClassName?: string;
  side?: ComponentProps<typeof TooltipContent>["side"];
  contentClassName?: string;
  /** Accessible name for the default help trigger. */
  label?: string;
  /** Controlled open state, for tooltips that follow a pointer instead of a hover on the trigger. */
  open?: boolean;
};

/**
 * Unified tooltip. Without `trigger` or `icon` it renders a small help icon
 * that explains something on hover or focus.
 */
export function CustomTooltip({
  children,
  trigger,
  icon: Icon,
  iconClassName,
  side,
  contentClassName,
  label = "Más información",
  open,
}: CustomTooltipProps) {
  return (
    <Tooltip open={open}>
      {trigger ? (
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      ) : Icon ? (
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center">
            <Icon className={cn("h-4 w-4", iconClassName)} />
          </span>
        </TooltipTrigger>
      ) : (
        <TooltipTrigger type="button" aria-label={label} className="shrink-0 self-center">
          <LuCircleHelp className={cn("h-3.5 w-3.5 text-gray-400", iconClassName)} />
        </TooltipTrigger>
      )}
      <TooltipContent side={side} className={cn("max-w-xs", contentClassName)}>
        {children}
      </TooltipContent>
    </Tooltip>
  );
}
